import React from 'react';
import styled from 'styled-components';
import AuthService from '../services/authService';
import theme from '../styles/theme';

const Header = styled.header`
  position: sticky;
  top: 0;
  z-index: 30;
  display: flex;
  min-height: 64px;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  border-bottom: 1px solid #e5e7eb;
  background: #ffffff;
  padding: env(safe-area-inset-top) 24px 0;
  box-shadow: 0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04);

  @media (max-width: 768px) {
    padding: env(safe-area-inset-top) 12px 0;
  }
`;

const Left = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  min-width: 0;
  flex: 1;

  @media (max-width: 768px) {
    gap: 8px;
  }
`;

const Title = styled.h1`
  font-size: 18px;
  font-weight: 600;
  color: #111827;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;

  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const Right = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  flex-shrink: 0;

  @media (max-width: 768px) {
    gap: 8px;
  }
`;

const Pill = styled.button`
  border-radius: 999px;
  border: 1px solid ${theme.primarySoftBorder};
  background: #f8fafc;
  color: #4b5563;
  font-size: 12px;
  padding: 6px 10px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: background 0.15s, border-color 0.15s, color 0.15s;
  &:hover { background: ${theme.primaryLight}; border-color: ${theme.primaryLightBorder}; color: #111827; }

  span:last-child {
    max-width: 220px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  @media (max-width: 768px) {
    padding: 6px 8px;
    span:last-child { max-width: 84px; }
  }

  @media (max-width: 480px) {
    span:last-child { display: none; }
  }
`;

const IconButton = styled.button`
  border: none;
  background: transparent;
  color: #4b5563;
  padding: 10px;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  transition: background 0.15s, color 0.15s;
  &:hover { background: ${theme.primaryLight}; color: ${theme.primaryDark}; }
`;

const Divider = styled.div`
  width: 1px;
  height: 28px;
  background: #e5e7eb;

  @media (max-width: 640px) {
    display: none;
  }
`;

const UserChip = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Avatar = styled.div`
  width: 34px;
  height: 34px;
  border-radius: 999px;
  background: ${theme.gradient};
  color: #ffffff;
  display: grid;
  place-items: center;
  font-size: 13px;
  font-weight: 700;
  flex-shrink: 0;
`;

const UserMeta = styled.div`
  display: flex;
  flex-direction: column;
  line-height: 1.25;

  @media (max-width: 640px) {
    display: none;
  }
`;

const UserName = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: #111827;
`;

const UserRole = styled.span`
  font-size: 11px;
  color: #6b7280;
`;

const TextButton = styled.button`
  border-radius: 999px;
  border: 1px solid #e5e7eb;
  background: #ffffff;
  color: #dc2626;
  font-size: 12px;
  padding: 6px 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  &:hover { background: #fef2f2; border-color: #fecaca; }

  @media (max-width: 640px) {
    padding: 8px;
    border-radius: 999px;
    span { display: none; }
  }
`;

export default function StudentTopBar({ title, onMenuClick }) {
  const user = AuthService.getUser() || {};
  const userInfo = AuthService.getUserInfo();
  const selected = user.selectedRegistration || null;
  const isParent = userInfo?.type === 'parent';

  const label = selected
    ? `${selected.courseShortName || selected.courseName || 'Course'} · ${selected.registrationNumber}`
    : 'No course selected';

  const displayName = userInfo?.studentName || (isParent ? 'Parent' : 'Student');
  const initials = displayName
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('') || 'S';

  const onChangeCourse = () => {
    window.location.replace('/app/select-course');
  };

  const onLogout = () => {
    try {
      AuthService.clear();
    } catch (_) {}
    window.location.replace('/login');
  };
  return (
    <Header>
      <Left>
        <IconButton onClick={onMenuClick} aria-label="Toggle menu">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16"/></svg>
        </IconButton>
        {title && <Title>{title}</Title>}
      </Left>
      <Right>
        <Pill type="button" onClick={onChangeCourse}>
          <span style={{ width: 8, height: 8, borderRadius: 999, background: '#22c55e', flexShrink: 0 }} />
          <span>{label}</span>
        </Pill>
        <Divider />
        <UserChip>
          <Avatar>{initials}</Avatar>
          <UserMeta>
            <UserName>{displayName}</UserName>
            <UserRole>{isParent ? 'Parent' : 'Student'}</UserRole>
          </UserMeta>
        </UserChip>
        <TextButton type="button" onClick={onLogout} aria-label="Logout">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"/></svg>
          <span>Logout</span>
        </TextButton>
      </Right>
    </Header>
  );
}
